import { h, type VNode } from 'vue';
import { toast, type Content, type Id, type ToastContentProps, type ToastOptions } from 'vue3-toastify';

import AlertContent from '@bv/components/alert/AlertContent.vue';
import { getLocale, trans } from '@bv/utils';

export type ToastPayload =
  | string
  | {
      message: string;
      action?: {
        text: string;
        onClick: () => void;
      };
    };

/**
 * BV toast options.
 *
 * Extends vue3-toastify's `ToastOptions` with `dedupe`, a convenience for
 * preventing the same toast from stacking when it is triggered repeatedly
 * (e.g. an alert shown on every login). It resolves to a stable `toastId`:
 *   - `dedupe: true`        -> id derived from the message text
 *   - `dedupe: 'some-key'`  -> uses that key as the id
 *
 * When a deduped toast is triggered again while it is still on screen,
 * it is updated in place — resetting the autoClose timer — so the user
 * notices the event recurred without duplicate toasts stacking.
 */
export type BvToastOptions = ToastOptions & {
  dedupe?: boolean | string;
};

type ToastVariant = 'info' | 'success' | 'warning' | 'error';

/**
 * A centralized composable for showing toast notifications.
 * Provides consistent, typed methods for different notification types.
 */
export function useToast() {
  const defaultOptions = (): ToastOptions => {
    const currentLocale = getLocale();

    return {
      position: currentLocale.isRtl ? toast.POSITION.TOP_LEFT : toast.POSITION.TOP_RIGHT,
      rtl: currentLocale.isRtl,
      icon: false,
      closeButton: false,
    };
  };

  /**
   * Merges defaults with caller options and, when `dedupe` is requested,
   * resolves it into a stable `toastId` so vue3-toastify suppresses duplicates.
   */
  const resolveOptions = (message: ToastPayload, { dedupe, ...options }: BvToastOptions): ToastOptions => {
    const merged: ToastOptions = { ...defaultOptions(), ...options };

    if (dedupe) {
      const text = typeof message === 'string' ? message : message.message;
      merged.toastId = merged.toastId ?? (dedupe === true ? text : dedupe);
    }

    return merged;
  };

  const emitByVariant: Record<ToastVariant, (content: Content, options: ToastOptions) => Id> = {
    success: (content, options) => toast.success(content, options),
    error: (content, options) => toast.error(content, options),
    info: (content, options) => toast.info(content, options),
    warning: (content, options) => toast.warn(content, options),
  };

  /**
   * Shared entry point: resolves options, and for a deduped toast that is still
   * on screen, updates it in place (resetting autoClose timer) instead of
   * letting vue3-toastify silently drop the repeat call.
   */
  const show = (variant: ToastVariant, message: ToastPayload, options: BvToastOptions): Id => {
    const resolved = resolveOptions(message, options);
    const { toastId } = resolved;

    if (toastId != null && toast.isActive(toastId)) {
      toast.update(toastId, resolved);
      return toastId;
    }

    return emitByVariant[variant](buildContent(variant, message), resolved);
  };

  const success = (message: ToastPayload, options: BvToastOptions = {}) => show('success', message, options);
  const error = (message: ToastPayload, options: BvToastOptions = {}) => show('error', message, options);
  const info = (message: ToastPayload, options: BvToastOptions = {}) => show('info', message, options);
  const warning = (message: ToastPayload, options: BvToastOptions = {}) => show('warning', message, options);

  /**
   * Shows a toast notification for a promise.
   * Automatically handles pending, success, and error states.
   * @param promise The promise to track.
   * @param messages The messages for each state.
   * @param options Optional toast configurations.
   */
  const promise = (
    promise: Promise<any>,
    messages: {
      pending: string;
      success: string;
      error: string;
    },
    options: ToastOptions = {},
  ) => {
    toast.promise(promise, messages, { ...defaultOptions(), ...options });
  };

  function buildContent(variant: ToastVariant, payload: ToastPayload): (props: ToastContentProps) => VNode {
    const normalized = typeof payload === 'string' ? { message: payload } : payload;
    const titleKeyByVariant: Record<typeof variant, string> = {
      info: 'global.toast.title.info_trans',
      success: 'global.toast.title.success_trans',
      warning: 'global.toast.title.warning_trans',
      error: 'global.toast.title.error_trans',
    };

    const title = trans(titleKeyByVariant[variant]);

    return ({ closeToast, toastProps }) => {
      const vnode = h(AlertContent, {
        variant,
        title,
        message: normalized.message,
        action: normalized.action,
        closeToast,
        autoClose: toastProps?.autoClose,
        pauseOnHover: toastProps?.pauseOnHover ?? true,
      });

      return vnode;
    };
  }

  return {
    success,
    error,
    info,
    warning,
    promise,
  };
}
